import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";

/**
 * Cloudflare Turnstile wrapper.
 *
 * Exposes a provider-agnostic `getToken(action)` API so the rest of the app
 * never imports Turnstile directly. The script is lazy-loaded on first call:
 * users who never hit a login page pay zero cost.
 *
 * Degraded mode: if the script fails to load (adblocker, CF outage, offline)
 * `getToken()` resolves with `null` instead of throwing - the backend IP rate
 * limiter + progressive lockout is the fallback. Login must never be blocked
 * by a third-party script failure.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          action?: string;
          // Cloudflare dropped "invisible" as a size value. Invisible mode
          // is now configured in the dashboard at site creation time. The
          // only valid sizes for the widget's visual dimensions are:
          size?: "normal" | "compact" | "flexible";
          callback?: (token: string) => void;
          "error-callback"?: (err: any) => void;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TOKEN_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: "root",
})
export class CaptchaService {
  private scriptPromise: Promise<void> | null = null;
  private hiddenHost: HTMLDivElement | null = null;

  get siteKey(): string {
    return (environment as any).turnstileSiteKey || "";
  }

  /**
   * Fetch a single-use Turnstile token for the given action.
   * Returns `null` on any failure (script blocked, timeout, expired, error).
   * Never throws - callers must handle the `null` case as degraded mode.
   */
  async getToken(action: string): Promise<string | null> {
    if (!this.siteKey) {
      console.warn("[CaptchaService] Missing turnstileSiteKey in environment");
      return null;
    }
    try {
      await this.loadScript();
    } catch (err) {
      console.warn("[CaptchaService] Failed to load Turnstile script", err);
      return null;
    }
    if (!window.turnstile) {
      return null;
    }
    return this.renderInvisible(action);
  }

  /**
   * Lazy script injector - memoized. Multiple concurrent callers share one
   * in-flight Promise so the <script> tag is only added once.
   */
  private loadScript(): Promise<void> {
    if (this.scriptPromise) return this.scriptPromise;

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      // Already loaded by a previous navigation?
      if (window.turnstile) {
        resolve();
        return;
      }
      const existing = document.querySelector<HTMLScriptElement>(
        'script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
      );
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("script error")));
        return;
      }
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.scriptPromise = null; // allow retry on next call
        reject(new Error("Turnstile script failed to load"));
      };
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  /**
   * Render a Turnstile widget into an off-screen host and wait for the
   * token callback. The widget is visually hidden via the off-screen host;
   * whether Turnstile actually shows a challenge UI depends on the widget
   * mode configured in the Cloudflare dashboard:
   *   - Mode "Invisible"      -> no UI ever, token returned silently
   *   - Mode "Managed"        -> UI only for suspicious traffic
   *   - Mode "Non-interactive" -> spinner shown but no challenge
   * Size is NOT set to "invisible" (Cloudflare rejects that value since
   * late 2024). The default normal size is fine because our host div is
   * positioned off-screen at left: -9999px.
   *
   * Times out after `TOKEN_TIMEOUT_MS` to prevent login UI from hanging.
   */
  private renderInvisible(action: string): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      let settled = false;
      const settle = (value: string | null) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      const host = this.getOrCreateHost();
      try {
        window.turnstile!.render(host, {
          sitekey: this.siteKey,
          action,
          callback: (token: string) => settle(token),
          "error-callback": () => settle(null),
          "expired-callback": () => settle(null),
          "timeout-callback": () => settle(null),
        });
      } catch (err) {
        console.warn("[CaptchaService] render() threw", err);
        settle(null);
        return;
      }

      // Hard timeout - if Turnstile never fires any callback, we must not
      // block the login form forever.
      setTimeout(() => settle(null), TOKEN_TIMEOUT_MS);
    });
  }

  private getOrCreateHost(): HTMLDivElement {
    if (this.hiddenHost && document.body.contains(this.hiddenHost)) {
      this.hiddenHost.innerHTML = "";
      return this.hiddenHost;
    }
    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.position = "fixed";
    host.style.left = "-9999px";
    host.style.top = "-9999px";
    host.style.width = "0";
    host.style.height = "0";
    host.style.overflow = "hidden";
    document.body.appendChild(host);
    this.hiddenHost = host;
    return host;
  }
}
